// Shared time-range filter vocabulary (W4, parity restore). Ported verbatim
// from Meteoric's src/lib/time-range.ts (KET gap analysis 2026-09-15) so both
// Profit Yield and Units Sold windows behave identically to the surface they
// restore parity with. The numeric values are DAY counts; "all" means no
// window. Monthly-grain data reinterprets these at month grain via
// `rangeMonths` / `grainWindow`.

export type RangeValue = "7" | "30" | "90" | "180" | "365" | "all";

export type TimeRangeOption = { value: RangeValue; label: string };

export const TIME_RANGE_OPTIONS: TimeRangeOption[] = [
  { value: "7", label: "7d" },
  { value: "30", label: "30d" },
  { value: "90", label: "90d" },
  { value: "180", label: "6mo" },
  { value: "365", label: "12mo" },
  { value: "all", label: "All time" },
];

// Dashboard pages default to the trailing 30-day window. A missing/invalid
// `?range=` uses this default.
export const DEFAULT_RANGE: RangeValue = "30";

export function isRangeValue(value: unknown): value is RangeValue {
  return (
    value === "7" || value === "30" || value === "90" || value === "180" || value === "365" || value === "all"
  );
}

export function parseRange(value: string | null | undefined): RangeValue {
  return isRangeValue(value) ? value : DEFAULT_RANGE;
}

export function rangeLabel(value: RangeValue): string {
  return TIME_RANGE_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

// Month-grain interpretation of a range for monthly data. Returns the number
// of trailing months to keep, or null for "all" (no limit).
// 6mo -> 6, 12mo -> 12, all -> null, 90d -> last 3 months, 30d/7d -> latest month.
export function rangeMonths(value: RangeValue): number | null {
  switch (value) {
    case "all":
      return null;
    case "365":
      return 12;
    case "180":
      return 6;
    case "90":
      return 3;
    case "30":
    case "7":
      return 1;
  }
}

// Slice a dated, oldest-to-newest monthly series down to the trailing window
// for a range. Pure; never mutates the input. `all` (or a window >= length)
// returns the series unchanged. Generic over any point carrying a month.
export function sliceSeriesByRange<T extends { m: string }>(series: T[], value: RangeValue): T[] {
  const months = rangeMonths(value);
  if (months === null || series.length <= months) {
    return series;
  }
  return series.slice(series.length - months);
}

// --- Time Period / granularity -----------------------------------
// The grain a chart point is denominated in. Independent of RANGE: RANGE
// windows *how much* history, grain denominates *what each point represents*.
export type GrainValue = "daily" | "weekly" | "monthly";

export type TimePeriodOption = { value: GrainValue; label: string };

export const TIME_PERIOD_OPTIONS: TimePeriodOption[] = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

export function isGrainValue(value: unknown): value is GrainValue {
  return value === "daily" || value === "weekly" || value === "monthly";
}

// Parse an explicit `?period=` choice. Returns the grain when the param is a
// valid grain, else null (absent/invalid) so the caller can fall back to the
// range default.
export function parseGrainParam(value: string | null | undefined): GrainValue | null {
  return isGrainValue(value) ? value : null;
}

export function grainLabel(value: GrainValue): string {
  return TIME_PERIOD_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

// The implied grain for a RANGE when the user has not explicitly chosen one:
// 7d/30d -> Daily, 90d -> Weekly, 180d/365d/all -> Monthly. Changing RANGE
// thus updates the implied grain until an explicit Time Period choice
// overrides it.
export function defaultGrainForRange(value: RangeValue): GrainValue {
  switch (value) {
    case "7":
    case "30":
      return "daily";
    case "90":
      return "weekly";
    case "180":
    case "365":
    case "all":
      return "monthly";
  }
}

// The grain actually in effect: an explicit, valid `?period=` wins;
// otherwise the range's default. `period` is the raw query-param string
// (null/absent allowed).
export function effectiveGrain(range: RangeValue, period: string | null | undefined): GrainValue {
  return parseGrainParam(period) ?? defaultGrainForRange(range);
}

// --- per-grain chart series selection -------------------------
// A tile's three per-grain series. Generic over any point carrying a
// month/date string.
export type GrainBundle<T> = { daily: T[]; weekly: T[]; monthly: T[] };

// Trailing-bucket count to keep for a (grain, range) pair. Monthly reuses
// rangeMonths; daily counts days; weekly counts whole weeks. `all` -> null
// (no window). The producer caps daily/weekly at whatever it backfills, so a
// wider range than the data simply returns everything available.
export function grainWindow(grain: GrainValue, range: RangeValue): number | null {
  if (grain === "monthly") {
    return rangeMonths(range);
  }
  if (range === "all") {
    return null;
  }
  const days = Number(range);
  return grain === "weekly" ? Math.ceil(days / 7) : days;
}

// The bucket key a reference date falls into, in the SAME string form the
// producer emits: daily/weekly -> 'YYYY-MM-DD' (weekly = Monday of the ISO
// week), monthly -> 'YYYY-MM'. Used to detect a still-open trailing bucket.
export function currentBucketKey(grain: GrainValue, reference: Date): string {
  const iso = reference.toISOString();
  if (grain === "monthly") {
    return iso.slice(0, 7);
  }
  if (grain === "daily") {
    return iso.slice(0, 10);
  }
  const monday = new Date(
    Date.UTC(reference.getUTCFullYear(), reference.getUTCMonth(), reference.getUTCDate()),
  );
  const mondayOffset = (monday.getUTCDay() + 6) % 7;
  monday.setUTCDate(monday.getUTCDate() - mondayOffset);
  return monday.toISOString().slice(0, 10);
}

// Drop the trailing point when its bucket is the one the reference date
// still sits inside (today / the current week / this month) - that bucket is
// partial, so plotting it draws a misleading cliff. Pure; never mutates
// input. `reference` is the data's freshness timestamp (generated_at), not
// wall-clock, so the bucket that was still accumulating when the snapshot
// was taken is the one trimmed.
export function trimPartialTrailing<T extends { m: string }>(
  series: T[],
  grain: GrainValue,
  reference: Date,
): T[] {
  const last = series.at(-1);
  return last && last.m === currentBucketKey(grain, reference) ? series.slice(0, -1) : series;
}

// Trim a partial trailing bucket then window a single dated series to the
// (grain, range) window. The grain-aware analogue of sliceSeriesByRange, used
// when a chart already holds one grain's series.
export function windowGrainSeries<T extends { m: string }>(
  series: T[],
  grain: GrainValue,
  range: RangeValue,
  reference: Date = new Date(),
): T[] {
  const trimmed = trimPartialTrailing(series, grain, reference);
  const window = grainWindow(grain, range);
  if (window === null || trimmed.length <= window) {
    return trimmed;
  }
  return trimmed.slice(trimmed.length - window);
}

// Pick the series for the effective grain, trim a partial trailing bucket,
// then window to the range. The single entry point pages use to chart any
// grain.
export function selectGrainSeries<T extends { m: string }>(
  bundle: GrainBundle<T>,
  grain: GrainValue,
  range: RangeValue,
  reference: Date = new Date(),
): T[] {
  const raw = grain === "daily" ? bundle.daily : grain === "weekly" ? bundle.weekly : bundle.monthly;
  return windowGrainSeries(raw, grain, range, reference);
}

// X-axis tick label for a bucket key at a grain: daily/weekly -> 'MM-DD'
// (weekly = the week-start date), monthly/undefined -> 'YY-MM' (the legacy
// default, so charts that pass no grain are unchanged).
export function formatGrainTick(m: string, grain?: GrainValue): string {
  return grain === "daily" || grain === "weekly" ? m.slice(5) : m.slice(2);
}

// The data's freshness timestamp as a Date, guarding an invalid/missing
// value (avoids `new Date('bad').toISOString()` throwing). Used as the trim
// reference.
export function freshnessDate(generatedAt: string | null | undefined): Date {
  if (generatedAt) {
    const parsed = new Date(generatedAt);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }
  return new Date();
}

// The reference for trimming a still-open trailing bucket: the DATA's own
// freshness date (the envelope's `meta.data_as_of`), never the artefact's
// write time. The producer runs on a Monday morning and writes `generated_at`
// = Monday, while the data it holds reaches only the previous Saturday - so
// against `generated_at` the week that just ended looks closed (its Sunday is
// still missing) and the Overview plotted a partial week the region tabs had
// correctly dropped (bug 3e90402f, 2026-09-28). `generated_at` is only the
// fallback for an envelope that carries no `data_as_of` at all.
export function dataReference(
  dataAsOf: string | null | undefined,
  generatedAt?: string | null,
): Date {
  return freshnessDate(dataAsOf || generatedAt);
}

// Empty-chart copy by grain: monthly keeps the legacy 'No trend yet';
// daily/weekly say the finer grain is not backfilled yet.
export function chartEmptyText(grain: GrainValue): string {
  return grain === "monthly" ? "No trend yet" : `${grainLabel(grain)} trend not available yet`;
}

// --- monthly boundary helpers (W1, parity polish) ------------------

// A `YYYY-MM` month key offset by `delta` whole months (negative moves
// earlier). Used to reason about a monthly series' freshness bound without
// re-deriving month arithmetic at every call site.
export function monthOffset(monthKey: string, delta: number): string {
  const [year, month] = monthKey.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1 + delta, 1));
  return date.toISOString().slice(0, 7);
}

// The last CLOSED month a monthly series should reach: one month before the
// freshness snapshot's own month. A monthly producer typically has not
// closed the month it is currently sitting inside, so that month is never
// the bound.
export function freshnessBoundMonth(reference: Date): string {
  return monthOffset(currentBucketKey("monthly", reference), -1);
}

export type SeriesIssue = { month: string; kind: "partial" | "stale" };

// The data-honesty issues a monthly series carries against a freshness
// snapshot (W9/W10). Takes the RAW producer series - the one `series` is
// BEFORE `trimPartialTrailing`/`selectGrainSeries` runs, not the windowed
// series a chart plots - because the still-open current-month bucket this
// function looks for is exactly the bucket the chart's own trim already
// removes. Feeding it a post-trim series makes the "partial" branch
// unreachable by construction. A trailing bucket that IS the still-open
// current month is "partial"; a last bucket behind the freshness bound is
// "stale" (a closed month the producer has not backfilled). Pure - reads
// the series and the reference only, invents nothing.
export function partialMonths<T extends { m: string }>(series: T[], reference: Date): SeriesIssue[] {
  const issues: SeriesIssue[] = [];
  const last = series.at(-1);
  if (!last) return issues;
  const current = currentBucketKey("monthly", reference);
  if (last.m === current) {
    issues.push({ month: last.m, kind: "partial" });
    return issues;
  }
  const bound = freshnessBoundMonth(reference);
  if (last.m < bound) {
    issues.push({ month: bound, kind: "stale" });
  }
  return issues;
}
